import { type DashRouteType, URL_RETAILER_SCOPED } from 'utils/routing/routes'

const validStoreConfigurationId = '\\d+'

export const URL_RETAILER_COLLECTIONS = `${URL_RETAILER_SCOPED}/retailer-collections`
export const URL_RETAILER_COLLECTIONS_V2 = `${URL_RETAILER_SCOPED}/retailer-collections/v2`

const URL_RETAILER_COLLECTIONS_BROWSE = `${URL_RETAILER_COLLECTIONS}/browse/:surface(${validStoreConfigurationId})?`

// This makes surface a required arg here, at this point this is necessary. Also reduces the chance of the routing confusing l1 with the surface
const URL_RETAILER_COLLECTIONS_STOREFRONT = `${URL_RETAILER_COLLECTIONS}/browse/:surface(${validStoreConfigurationId})`
const URL_RETAILER_COLLECTIONS_LIST = `${URL_RETAILER_COLLECTIONS_STOREFRONT}`
// NOTE, this will support up to 5 levels, add more accordingly.
const URL_RETAILER_COLLECTIONS_VIEW = `${URL_RETAILER_COLLECTIONS}/browse/:surface(${validStoreConfigurationId})/:l1(\\d+)/:l2(\\d+)?/:l3(\\d+)?/:l4(\\d+)?/:l5(\\d+)?`
const URL_RETAILER_COLLECTIONS_VIEW_DETAILS = `${URL_RETAILER_COLLECTIONS_VIEW}/details`
const URL_RETAILER_COLLECTIONS_VIEW_PRODUCTS = `${URL_RETAILER_COLLECTIONS_VIEW}/products`
const URL_RETAILER_COLLECTIONS_VIEW_LIST = `${URL_RETAILER_COLLECTIONS_VIEW}/list`

const URL_RETAILER_COLLECTIONS_INSTACART_VIEW = `${URL_RETAILER_COLLECTIONS}/browse/:surface(${validStoreConfigurationId})/ic/:l1(c\\d+)/:l2([cs]\\d+)?/:l3(s\\d+)?`
const URL_RETAILER_COLLECTIONS_INSTACART_VIEW_LIST = `${URL_RETAILER_COLLECTIONS_INSTACART_VIEW}/list`
const URL_RETAILER_COLLECTIONS_INSTACART_VIEW_PRODUCTS = `${URL_RETAILER_COLLECTIONS_INSTACART_VIEW}/products`
const URL_RETAILER_COLLECTIONS_INSTACART_VIEW_DETAILS = `${URL_RETAILER_COLLECTIONS_INSTACART_VIEW}/details`

const URL_RETAILER_COLLECTIONS_CREATE = `${URL_RETAILER_COLLECTIONS}/create`
const URL_RETAILER_COLLECTIONS_EDIT = `${URL_RETAILER_COLLECTIONS}/edit/:id`
const URL_RETAILER_COLLECTIONS_DUPLICATE = `${URL_RETAILER_COLLECTIONS}/duplicate/:id`
const URL_INSTACART_COLLECTIONS_EDIT = `${URL_RETAILER_COLLECTIONS}/ic-edit/:id`
const URL_INSTACART_COLLECTIONS_DUPLICATE = `${URL_RETAILER_COLLECTIONS}/ic-duplicate/:id`

export const URL_RETAILER_COLLECTIONS_BULK_UPLOAD_STRUCTURE = `${URL_RETAILER_COLLECTIONS}/bulk-upload-structure`
export const URL_RETAILER_COLLECTIONS_BULK_UPLOAD_PRODUCTS = `${URL_RETAILER_COLLECTIONS}/bulk-upload-products`

const URL_RETAILER_COLLECTIONS_V2_LIST = `${URL_RETAILER_COLLECTIONS_V2}/list`
const URL_RETAILER_COLLECTIONS_V2_CREATE = `${URL_RETAILER_COLLECTIONS_V2}/create`
const URL_RETAILER_COLLECTIONS_V2_VIEW = `${URL_RETAILER_COLLECTIONS_V2}/view/:id`
const URL_RETAILER_COLLECTIONS_V2_EDIT = `${URL_RETAILER_COLLECTIONS_V2}/edit/:id`
const URL_RETAILER_COLLECTIONS_V2_BULK_UPLOAD_PRODUCTS = `${URL_RETAILER_COLLECTIONS_V2}/bulk-upload-products`
// @hygen:inject scoped-urls

export type RetailerCollectionsBrowseParams = { surface?: string }
export type RetailerCollectionsViewParams = RetailerCollectionsBrowseParams & {
  l1: string
  l2?: string
  l3?: string
  l4?: string
  l5?: string
}

export type RetailerCollectionsEditParams = {
  id: string
}

export type RetailerCollectionsDuplicateParams = {
  id: string
}

export type InstacartCollectionsEditParams = {
  id: string
}

export type InstacartCollectionsDuplicateParams = {
  id: string
}

export type RetailerCollectionsV2ViewParams = {
  id: string
}

export type RetailerCollectionsV2EditParams = {
  id: string
}

export type RetailerCollectionsInstacartViewParams = RetailerCollectionsBrowseParams & {
  l1: string
  l2?: string
  l3?: string
}

export type DashRouteSet =
  | DashRouteType<'retailer-collections'>
  | DashRouteType<'retailer-collections-list'>
  | DashRouteType<'retailer-collections-browse', RetailerCollectionsBrowseParams>
  | DashRouteType<'retailer-collections-view', RetailerCollectionsViewParams>
  | DashRouteType<'retailer-collections-view-details', RetailerCollectionsViewParams>
  | DashRouteType<'retailer-collections-view-products', RetailerCollectionsViewParams>
  | DashRouteType<'retailer-collections-view-list'>
  | DashRouteType<'retailer-collections-create'>
  | DashRouteType<'retailer-collections-edit', RetailerCollectionsEditParams>
  | DashRouteType<'retailer-collections-duplicate', RetailerCollectionsDuplicateParams>
  | DashRouteType<'retailer-collections-ic-edit', InstacartCollectionsEditParams>
  | DashRouteType<'retailer-collections-ic-duplicate', InstacartCollectionsDuplicateParams>
  | DashRouteType<'retailer-collections-bulk-upload-structure'>
  | DashRouteType<'retailer-collections-bulk-upload-products'>
  | DashRouteType<'retailer-collections-instacart-view', RetailerCollectionsInstacartViewParams>
  | DashRouteType<
      'retailer-collections-instacart-view-list',
      RetailerCollectionsInstacartViewParams
    >
  | DashRouteType<
      'retailer-collections-instacart-view-products',
      RetailerCollectionsInstacartViewParams
    >
  | DashRouteType<
      'retailer-collections-instacart-view-details',
      RetailerCollectionsInstacartViewParams
    >
  | DashRouteType<'retailer-collections-v2'>
  | DashRouteType<'retailer-collections-v2-list'>
  | DashRouteType<'retailer-collections-v2-create'>
  | DashRouteType<'retailer-collections-v2-view'>
  | DashRouteType<'retailer-collections-v2-edit'>
  | DashRouteType<'retailer-collections-v2-bulk-upload-products'>
// @hygen:inject dash-route-set

export const routes: { [Key in DashRouteSet['identifier']]: string } = {
  'retailer-collections': URL_RETAILER_COLLECTIONS,
  'retailer-collections-browse': URL_RETAILER_COLLECTIONS_BROWSE,
  'retailer-collections-list': URL_RETAILER_COLLECTIONS_LIST,
  'retailer-collections-view': URL_RETAILER_COLLECTIONS_VIEW,
  'retailer-collections-view-details': URL_RETAILER_COLLECTIONS_VIEW_DETAILS,
  'retailer-collections-view-products': URL_RETAILER_COLLECTIONS_VIEW_PRODUCTS,
  'retailer-collections-view-list': URL_RETAILER_COLLECTIONS_VIEW_LIST,
  'retailer-collections-create': URL_RETAILER_COLLECTIONS_CREATE,
  'retailer-collections-edit': URL_RETAILER_COLLECTIONS_EDIT,
  'retailer-collections-duplicate': URL_RETAILER_COLLECTIONS_DUPLICATE,
  'retailer-collections-ic-edit': URL_INSTACART_COLLECTIONS_EDIT,
  'retailer-collections-ic-duplicate': URL_INSTACART_COLLECTIONS_DUPLICATE,
  'retailer-collections-bulk-upload-structure': URL_RETAILER_COLLECTIONS_BULK_UPLOAD_STRUCTURE,
  'retailer-collections-bulk-upload-products': URL_RETAILER_COLLECTIONS_BULK_UPLOAD_PRODUCTS,
  'retailer-collections-instacart-view': URL_RETAILER_COLLECTIONS_INSTACART_VIEW,
  'retailer-collections-instacart-view-list': URL_RETAILER_COLLECTIONS_INSTACART_VIEW_LIST,
  'retailer-collections-instacart-view-products': URL_RETAILER_COLLECTIONS_INSTACART_VIEW_PRODUCTS,
  'retailer-collections-instacart-view-details': URL_RETAILER_COLLECTIONS_INSTACART_VIEW_DETAILS,
  'retailer-collections-v2': URL_RETAILER_COLLECTIONS_V2,
  'retailer-collections-v2-list': URL_RETAILER_COLLECTIONS_V2_LIST,
  'retailer-collections-v2-create': URL_RETAILER_COLLECTIONS_V2_CREATE,
  'retailer-collections-v2-view': URL_RETAILER_COLLECTIONS_V2_VIEW,
  'retailer-collections-v2-edit': URL_RETAILER_COLLECTIONS_V2_EDIT,
  'retailer-collections-v2-bulk-upload-products': URL_RETAILER_COLLECTIONS_V2_BULK_UPLOAD_PRODUCTS,
  // @hygen:inject route-pair
}

export type DomainRouteName = keyof typeof routes
